import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import parties from '../data/party';
import stockdata from '../data/stock';
import sellstocks from '../data/sellstocks';

/* types remain unchanged */
interface Party {
  id: number;
  name: string;
  total_amount?: number | string;
}
type SellStockRecord = {
  id: number;
  party_id: number;
  category: string;
  stock_id: string;
  stock_name: string;
  sell_units: string;
  sell_amount: string;
  indirect_charges: string;
  date: string;
  catrgories?: string;
};
interface StockDBCategoryItem {
  id: number | string;
  investment_type: string;
  category: string;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
}
type SellStockFormState = {
  id?: number;
  partyId: string;
  category: string;
  stockId: string | number;
  stockName: string;
  sellUnits: string;
  sellAmount: string;
  indirectCharges: string;
  date: string;
};

// Light green color palette
const LG_PRIMARY = '#1b3b1b';
const LG_ACCENT = '#b3ffd3';
const LG_SECONDARY = '#5ad58b';
const LG_MAIN_BG = '#f6fbf8';
const LG_MAIN_FG = '#175c2b';
const LG_BORDER = '#d7eddc';
const LG_SUBTLE = '#e3fbe8';
const LG_INPUT_BG = '#e5faf1';
const LG_BTN_BG = '#5ad58b';
const LG_BTN_BG_HOVER = '#b3ffd3';
const LG_BTN_TEXT = '#135627';

async function fetchSellStocks(): Promise<SellStockRecord[]> {
  try {
    const res = await sellstocks();
    if (res && typeof res.json === 'function') {
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } else if (Array.isArray(res)) {
      return res;
    }
    return [];
  } catch {
    return [];
  }
}

function getSellUnitsForStock(
  sellStocks: SellStockRecord[],
  partyId: string,
  category: string,
  stockId: string | number
): number {
  return sellStocks.reduce((sum, rec) => {
    if (
      String(rec.party_id) === String(partyId) &&
      String(rec.category) === String(category) &&
      (String(rec.stock_id) === String(stockId) || String(rec.stock_name) === String(stockId))
    ) {
      return sum + parseFloat(rec.sell_units || '0');
    }
    return sum;
  }, 0);
}

function getPartyStocksAdjusted(
  stocksDbRows: any[],
  sellStocks: SellStockRecord[],
  partyId: string
) {
  let result: { [category: string]: StockDBCategoryItem[] } = {};
  for (const row of stocksDbRows) {
    if (String(row.party_id) !== String(partyId)) continue;
    let catsArr: StockDBCategoryItem[] = [];
    if (typeof row.categories === 'string') {
      try {
        const parsed = JSON.parse(row.categories);
        catsArr = Array.isArray(parsed) ? parsed : [];
      } catch {
        catsArr = [];
      }
    } else if (Array.isArray(row.categories)) {
      catsArr = row.categories as StockDBCategoryItem[];
    }
    for (const item of catsArr) {
      const soldUnits = getSellUnitsForStock(
        sellStocks,
        partyId,
        item.category,
        item.id
      );
      let origUnit = parseFloat(item.unit || '0');
      let remainingUnit = origUnit - soldUnits;
      const adjustedItem = {
        ...item,
        unit: remainingUnit >= 0 ? String(remainingUnit) : '0',
      };
      if (!result[item.category]) result[item.category] = [];
      result[item.category].push(adjustedItem);
    }
  }
  return result;
}

function getStockOptionKey(stock: StockDBCategoryItem) {
  return `${stock.category}__${stock.id}__${stock.stock_name}`;
}

const EditSell = () => {
  const [partiesList, setPartiesList] = useState<Party[]>([]);
  const [sellStocks, setSellStocks] = useState<SellStockRecord[]>([]);
  const [stocksDataDb, setStocksDataDb] = useState<any[]>([]);
  const [totalamountspend, setTotalAmmountSpend] = useState<Number>();
  const [sellStockForm, setSellStockForm] = useState<SellStockFormState>({
    partyId: '',
    category: '',
    stockId: '',
    stockName: '',
    sellUnits: '',
    sellAmount: '',
    indirectCharges: '',
    date: '',
  });
  const [autoAmount, setAutoAmount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [editingSellId, setEditingSellId] = useState<number | undefined>(undefined);

  const prevPageRef = useRef<string>(document.referrer || "/usershistory");

  useEffect(() => {
    async function loadSellStocks() {
      const sellArr = await fetchSellStocks();
      setSellStocks(sellArr);
    }
    loadSellStocks();
  }, []);
  useEffect(() => {
    (async () => {
      const pts = await parties();
      setPartiesList(Array.isArray(pts) ? pts : []);
      const db = await stockdata();
      setStocksDataDb(Array.isArray(db) ? db : []);
    })();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const partyId = urlParams.get('party_id') || '';
    const category = urlParams.get('category') || '';
    const stockId = urlParams.get('stock_id') || '';
    if (!partyId || !category || !stockId || !sellStocks.length) return;

    const found = sellStocks.find(
      s =>
        String(s.party_id) === String(partyId) &&
        String(s.category) === String(category) &&
        (
          String(s.stock_id) === String(stockId) ||
          String(s.stock_name) === String(stockId)
        )
    );
    if (found) {
      setEditingSellId(found.id);
      setSellStockForm({
        id: found.id,
        partyId: String(found.party_id),
        category: found.category,
        stockId: found.stock_id ?? found.stock_name,
        stockName: found.stock_name,
        sellUnits: found.sell_units,
        sellAmount: found.sell_amount,
        indirectCharges: found.indirect_charges,
        date: found.date ? new Date(found.date).toISOString().slice(0, 10) : "",
      });
    } else {
      setSellStockForm(s => ({
        ...s,
        partyId: String(partyId),
        category: String(category),
        stockId: String(stockId),
      }));
    }
  }, [sellStocks]);

  const partyStocksByCat = useMemo(
    () => getPartyStocksAdjusted(stocksDataDb, sellStocks, sellStockForm.partyId),
    [stocksDataDb, sellStocks, sellStockForm.partyId]
  );
  const partyCategories = useMemo(
    () => Object.keys(partyStocksByCat),
    [partyStocksByCat]
  );
  const stocksInCurrentCategory = useMemo(
    () =>
      sellStockForm.category
        ? (partyStocksByCat[sellStockForm.category] || []).filter(
            s => !!(s.stock_name && s.stock_name.trim())
          )
        : [],
    [sellStockForm.category, partyStocksByCat]
  );

  const selectedStockObj = useMemo(
    () =>
      stocksInCurrentCategory.find(
        (s) =>
          String(s.id) === String(sellStockForm.stockId) ||
          String(s.stock_name) === String(sellStockForm.stockName)
      ),
    [sellStockForm.stockId, sellStockForm.stockName, stocksInCurrentCategory]
  );

  useEffect(() => {
    const unitsNum = parseFloat(sellStockForm.sellUnits || '0');
    if (!selectedStockObj || isNaN(unitsNum) || unitsNum <= 0) {
      setAutoAmount(0);
      return;
    }
    const totalAvailableUnits = parseFloat(selectedStockObj.unit);
    const amount = parseFloat(selectedStockObj.total_amount);
    let amt = totalAvailableUnits > 0 ? (amount / totalAvailableUnits) * unitsNum : 0;
    setAutoAmount(amt);
  }, [sellStockForm.sellUnits, selectedStockObj]);

  const fetchUserRemainingBalance = useCallback(async (partyId: string) => {
    if (!partyId) {
      setTotalAmmountSpend(0);
      return;
    }
    try {
      const usermetaResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta`);
      const usermetaList = await usermetaResponse.json();
      const foundMeta = usermetaList.find(
        (meta: any) => meta.meta_key === "remaining" && String(meta.party_id) === String(partyId)
      );
      if (foundMeta) {
        setTotalAmmountSpend(Number(foundMeta.meta_value));
      } else {
        const party = partiesList.find(p => String(p.id) === String(partyId));
        setTotalAmmountSpend(Number(party?.total_amount || 0));
      }
    } catch (err) {
      console.error("Error fetching usermeta:", err);
      setTotalAmmountSpend(0);
    }
  }, [partiesList]);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const partyId = urlParams.get('party_id') || '';
    fetchUserRemainingBalance(partyId);
  }, [fetchUserRemainingBalance]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "sellUnits") {
      if (selectedStockObj) {
        const maxUnits = parseFloat(selectedStockObj.unit || "0");
        if (value === "" || value === undefined) {
          setSellStockForm(s => ({ ...s, sellUnits: "" }));
          return;
        }
        const entered = parseFloat(value);
        if (isNaN(entered) || entered < 0) {
          return;
        }
        if (entered > maxUnits) {
          setSellStockForm(s => ({ ...s, sellUnits: maxUnits.toString() }));
          return;
        }
        setSellStockForm(s => ({ ...s, sellUnits: value }));
        return;
      }
    }
    if (name === "sellAmount") {
      if (selectedStockObj) {
        if (value === "" || value === undefined) {
          setSellStockForm(s => ({ ...s, sellAmount: "" }));
          return;
        }
        const entered = parseFloat(value);
        if (isNaN(entered) || entered < 0) {
          return;
        }
        setSellStockForm(s => ({ ...s, sellAmount: value }));
        return;
      }
    }

    let patch: Partial<SellStockFormState> = { [name]: value };
    if (name === "partyId") {
      patch.category = '';
      patch.stockId = '';
      patch.stockName = '';
      patch.sellUnits = '';
      patch.sellAmount = '';
      patch.date = '';
    }
    if (name === "category") {
      patch.stockId = '';
      patch.stockName = '';
      patch.sellUnits = '';
      patch.sellAmount = '';
    }
    if (name === "stockId") {
      const found = stocksInCurrentCategory.find(s => String(s.stock_name) === value);
      patch.stockName = found ? found.stock_name : '';
      patch.stockId = found ? found.stock_name : '';
      patch.sellUnits = '';
      patch.sellAmount = '';
    }
    setSellStockForm(s => ({ ...s, ...patch }));
  };

  const handleGoBack = (e?: React.MouseEvent) => {
    e && e.preventDefault();
    if (window.history.length > 1 && document.referrer) {
      window.history.back();
    } else {
      window.location.href = prevPageRef.current || "/usershistory";
    }
  };

  const handleSellStockUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sellStockForm.partyId) return alert('Select party');
    if (!sellStockForm.category) return alert('Select category');
    if (!sellStockForm.stockId) return alert('Select stock');
    const sellUnitsNumber = parseFloat(sellStockForm.sellUnits || '0');
    if (!sellUnitsNumber || isNaN(sellUnitsNumber) || sellUnitsNumber <= 0)
      return alert('Please enter valid units to sell');
    if (!selectedStockObj) return alert('Select valid stock');
    if (sellUnitsNumber > parseFloat(selectedStockObj.unit || '0')) {
      return alert(
        `Not enough units available to sell! Available: ${selectedStockObj.unit}, Requested: ${sellUnitsNumber}`
      );
    }
    if (!sellStockForm.date) return alert('Select date for sell');

    const sellAmount = Number(sellStockForm.sellAmount) > 0 ? Number(sellStockForm.sellAmount) : (typeof autoAmount !== 'undefined' ? Number(autoAmount) : 0);
    const indirectCharges = Number(sellStockForm.indirectCharges) || 0;
    const netSale = sellAmount - indirectCharges;

    const handleUserMetaUpdate = async () => {
      try {
        const usermetaResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/updateusermeta?party_id=${sellStockForm.partyId}`
        );
        const usermetaList = await usermetaResponse.json();
        const foundMeta = Array.isArray(usermetaList)
          ? usermetaList.find(
              (meta: any) =>
                meta.meta_key === 'remaining' &&
                String(meta.party_id) === String(sellStockForm.partyId)
            )
          : undefined;
        if (foundMeta) {
          const oldVal = parseFloat(foundMeta.meta_value || '0');
          const meta_value = (oldVal + netSale).toString();
          await fetch(
            `${process.env.REACT_APP_API_URL}/auth/updateusermeta/${foundMeta.id}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                meta_key: 'remaining',
                meta_value,
                party_id: sellStockForm.partyId,
              }),
            }
          );
        } else {
          const oldVal = Number(totalamountspend) || 0;
          const remaining = (oldVal + netSale).toString();
          await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              meta_key: 'remaining',
              meta_value: remaining,
              party_id: sellStockForm.partyId,
            }),
          });
        }
      } catch (err) {
        console.error('Usermeta API failed', err);
      }
    };

    setLoading(true);
    try {
      if (!editingSellId) {
        alert("Could not identify the sell record to edit (missing id)");
        setLoading(false);
        return;
      }
      const payload = {
        party_id: sellStockForm.partyId,
        category: sellStockForm.category,
        stock_id: sellStockForm.stockId,
        stock_name: sellStockForm.stockName,
        sell_units: sellUnitsNumber,
        sell_amount: sellAmount,
        indirect_charges: sellStockForm.indirectCharges,
        date: sellStockForm.date,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/sellstocks/${editingSellId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result?.message || 'Failed to update sell stock');

      setTotalAmmountSpend(prev => (Number(prev) || 0) + netSale);

      await handleUserMetaUpdate();

      alert('Stock sell updated successfully!');
      setTimeout(() => {
        if (window.history.length > 1 && document.referrer) {
          window.history.back();
          setTimeout(() => window.location.reload(), 500);
        } else {
          window.location.href = prevPageRef.current || "/usershistory";
        }
      }, 500);
    } catch (err: any) {
      alert('Error: ' + (err?.message ?? 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  // --- MIDDLE SIZE form with multi-column layout, light green theme ---
  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: LG_MAIN_BG,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        padding: "0px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1060,
          background: "#fff",
          borderRadius: 10,
          border: `1.5px solid ${LG_BORDER}`,
          margin: "0px",
          boxShadow: "0 4px 16px 0 rgba(39,38,44,0.07)",
          padding: 0,
          overflow: "hidden",
        }}
        className="shadow"
      >
        {/* Header */}
        <div
          style={{
            width: "100%",
            background: LG_SECONDARY,
            color: LG_BTN_TEXT,
            padding: "18px 36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            aria-label="Back to Previous"
            title="Go Back"
            onClick={handleGoBack}
            style={{
              background: LG_SUBTLE,
              color: LG_PRIMARY,
              fontWeight: 600,
              padding: "6px 18px",
              borderRadius: 7,
              border: `1.5px solid ${LG_ACCENT}`,
              fontSize: 15,
              cursor: "pointer",
              outline: "none",
              transition: "background 0.18s",
            }}
            onMouseOver={e => {
              (e.currentTarget as HTMLElement).style.background = LG_ACCENT;
              (e.currentTarget as HTMLElement).style.color = LG_BTN_TEXT;
            }}
            onMouseOut={e => {
              (e.currentTarget as HTMLElement).style.background = LG_SUBTLE;
              (e.currentTarget as HTMLElement).style.color = LG_PRIMARY;
            }}
          >
            ← Back
          </button>
          <h2
            style={{
              fontSize: 27,
              fontWeight: 900,
              letterSpacing: ".5px",
              color: LG_MAIN_FG,
              margin: 0,
              padding: 0,
            }}
          >
            Edit Sell Stock
          </h2>
        </div>

        <form
          onSubmit={handleSellStockUpdate}
          autoComplete="off"
          style={{ padding: "32px 40px" }}
        >
          {/* Row 1: Party, Category */}
          <div style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 28,
            marginBottom: 22,
            width: '100%',
            alignItems: 'flex-start'
          }}>
            {/* Party */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="partyId"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 6,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Party{" "}
                <span style={{ fontWeight: 400, fontSize: 13, color: LG_SECONDARY }}>
                  (not editable)
                </span>
              </label>
              <select
                name="partyId"
                id="partyId"
                value={sellStockForm.partyId}
                onChange={handleFormChange}
                required
                disabled
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  marginTop: 2,
                  background: LG_INPUT_BG,
                  color: LG_MAIN_FG,
                }}
              >
                <option value="">Choose Party</option>
                {partiesList.map((p) => (
                  <option key={String(p.id)} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Category */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="category"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 6,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Category
              </label>
              <select
                name="category"
                id="category"
                value={sellStockForm.category}
                onChange={handleFormChange}
                required
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  marginTop: 2,
                  background: "#fff",
                  color: LG_MAIN_FG,
                }}
              >
                <option value="">Select Category</option>
                {partyCategories.map((cat) => (
                  <option key={String(cat)} value={cat}>
                    {cat}
                  </option>
                ))}
                {!partyCategories.includes(sellStockForm.category) && (
                  <option value={sellStockForm.category}>
                    {sellStockForm.category}
                  </option>
                )}
              </select>
            </div>
          </div>

          {/* Row 2: Stock, Units */}
          <div style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 28,
            marginBottom: 22,
            width: '100%',
            alignItems: 'flex-start'
          }}>
            {/* Stock */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="stockId"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 6,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Stock Name
              </label>
              <select
                name="stockId"
                id="stockId"
                value={sellStockForm.stockId}
                onChange={handleFormChange}
                required
                disabled={!sellStockForm.category}
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  background: "#fff",
                  color: LG_MAIN_FG,
                }}
              >
                <option value="">Select Stock</option>
                {stocksInCurrentCategory.map(
                  (stock) =>
                    !!(stock.stock_name && stock.stock_name.trim()) && (
                      <option
                        key={getStockOptionKey(stock)}
                        value={stock.stock_name}
                      >
                        {stock.stock_name}
                      </option>
                    )
                )}
                {!stocksInCurrentCategory.some(
                  (stock) =>
                    String(stock.stock_name) === String(sellStockForm.stockId)
                ) && (
                  <option value={sellStockForm.stockId}>
                    {sellStockForm.stockName || sellStockForm.stockId}
                  </option>
                )}
              </select>
              {sellStockForm.category && stocksInCurrentCategory.length === 0 && (
                <div style={{ marginTop: 5, fontSize: 13, color: LG_SECONDARY }}>
                  No stocks found in this category.
                </div>
              )}
            </div>
            {/* Units */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="sellUnits"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 4,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Units To Sell{" "}
                {selectedStockObj && (
                  <span style={{
                    marginLeft: 7,
                    fontSize: 13,
                    color: LG_SECONDARY,
                    fontWeight: 400,
                  }}>
                    (Available: {selectedStockObj.unit})
                  </span>
                )}
              </label>
              <input
                type="number"
                name="sellUnits"
                id="sellUnits"
                value={sellStockForm.sellUnits}
                required
                min={0}
                max={selectedStockObj ? selectedStockObj.unit : undefined}
                onChange={handleFormChange}
                placeholder="Units to sell"
                inputMode="decimal"
                pattern="[0-9]*"
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  background: LG_INPUT_BG,
                  color: LG_MAIN_FG,
                }}
              />
              {sellStockForm.sellUnits &&
                selectedStockObj &&
                parseFloat(sellStockForm.sellUnits) >
                  parseFloat(selectedStockObj.unit) ? (
                  <span style={{ fontSize: 13, color: LG_SECONDARY, fontWeight: 500 }}>
                    Not enough units available!
                  </span>
                ) : null}
            </div>
          </div>

          {/* Row 3: Sell Amount, Indirect Charges, Date */}
          <div style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 28,
            marginBottom: 22,
            width: '100%',
            alignItems: 'flex-start'
          }}>
            {/* Sell Amount */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="sellAmount"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 4,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Sell Amount
              </label>
              <input
                type="number"
                name="sellAmount"
                id="sellAmount"
                placeholder={`Auto: ${(autoAmount || 0).toFixed(2)}`}
                value={sellStockForm.sellAmount}
                min={0}
                step="any"
                onChange={handleFormChange}
                required
                inputMode="decimal"
                pattern="[0-9]*"
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  background: "#fff",
                  color: LG_MAIN_FG,
                }}
              />
              <span
                style={{
                  fontSize: 13,
                  color: LG_SECONDARY,
                  marginLeft: 2,
                }}
              >
                Leave blank for auto. Current auto: {(autoAmount || 0).toFixed(2)}
              </span>
            </div>
            {/* Indirect Charges */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="indirectCharges"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 4,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Indirect Charges
              </label>
              <input
                type="number"
                name="indirectCharges"
                id="indirectCharges"
                placeholder="Charges (if any)"
                value={sellStockForm.indirectCharges}
                min={0}
                step="any"
                onChange={handleFormChange}
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  background: LG_INPUT_BG,
                  color: LG_MAIN_FG,
                }}
              />
            </div>
            {/* Date */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label
                htmlFor="date"
                style={{
                  fontWeight: 500,
                  color: LG_MAIN_FG,
                  marginBottom: 4,
                  display: "block",
                  fontSize: 15,
                  letterSpacing: 0.01,
                }}
              >
                Date
              </label>
              <input
                type="date"
                name="date"
                id="date"
                value={sellStockForm.date}
                onChange={handleFormChange}
                required
                style={{
                  width: "100%",
                  border: `1.5px solid ${LG_BORDER}`,
                  borderRadius: 6,
                  padding: "11px 9px",
                  fontSize: 16,
                  background: "#fff",
                  color: LG_MAIN_FG,
                }}
              />
            </div>
          </div>

          {/* Submit/Cancel buttons */}
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 14,
              marginTop: 16,
            }}
          >
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 34px",
                background: loading ? LG_BTN_BG_HOVER : LG_BTN_BG,
                color: LG_BTN_TEXT,
                fontWeight: 700,
                border: `2px solid ${LG_SECONDARY}`,
                borderRadius: 7,
                fontSize: 16,
                letterSpacing: 0.01,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                outline: "none",
                transition: "background 0.11s, opacity 0.11s",
              }}
              onMouseOver={e => {
                if (!loading)
                  (e.currentTarget as HTMLElement).style.background = LG_BTN_BG_HOVER;
              }}
              onMouseOut={e => {
                if (!loading)
                  (e.currentTarget as HTMLElement).style.background = LG_BTN_BG;
              }}
            >
              {loading ? "Saving..." : "Update & Go Back"}
            </button>
            <button
              type="button"
              onClick={handleGoBack}
              style={{
                padding: "12px 27px",
                background: "#fff",
                color: LG_SECONDARY,
                fontWeight: 600,
                borderRadius: 7,
                border: `1.5px solid ${LG_SECONDARY}`,
                fontSize: 15,
                cursor: "pointer",
              }}
              onMouseOver={e => {
                (e.currentTarget as HTMLElement).style.background = LG_SUBTLE;
              }}
              onMouseOut={e => {
                (e.currentTarget as HTMLElement).style.background = "#fff";
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSell;
